<template>
    <div>
        <div class="text-center mb-6">
            <v-icon size="48" color="primary" class="mb-2">mdi-shape-outline</v-icon>
            <h3 class="text-h6 font-weight-bold">What Are You Tracking?</h3>
            <p class="text-body-2 text-grey-darken-1">Choose what kind of thing you're tracking with this device</p>
        </div>

        <v-alert v-if="loadError" type="warning" variant="tonal" class="mb-4">
            {{ loadError }}
            <template #append>
                <v-btn variant="text" size="small" @click="loadAssetTypes">Retry</v-btn>
            </template>
        </v-alert>

        <div v-if="loading" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="primary"/>
        </div>

        <div v-else-if="assetTypes.length > 0" class="asset-type-grid">
            <v-card
                v-for="assetType in assetTypes"
                :key="assetType.id"
                :class="{'selected-type': selectedType?.id === assetType.id}"
                class="asset-type-card pa-4 text-center cursor-pointer"
                :elevation="selectedType?.id === assetType.id ? 4 : 1"
                rounded="lg"
                @click="selectType(assetType)"
            >
                <v-icon
                    size="36"
                    :color="selectedType?.id === assetType.id ? 'primary' : 'grey-darken-1'"
                    class="mb-2"
                >
                    {{ getIcon(assetType) }}
                </v-icon>
                <div class="text-body-2 font-weight-medium">{{ assetType.name }}</div>
                <div v-if="assetType.category" class="text-caption text-grey">{{ assetType.category }}</div>
            </v-card>
        </div>

        <v-alert v-else-if="!loading && !loadError" type="info" variant="tonal" class="mb-4">
            No asset types are available. Please contact your administrator to set up asset types.
        </v-alert>

        <v-btn
            color="primary"
            size="large"
            block
            class="mt-6"
            :disabled="!selectedType"
            @click="$emit('selected', selectedType)"
        >
            Continue
            <v-icon end>mdi-arrow-right</v-icon>
        </v-btn>
    </div>
</template>

<script setup>
import {ref, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";

const emit = defineEmits(['selected']);
const snackbar = useSnackbarStore();

const assetTypes = ref([]);
const selectedType = ref(null);
const loading = ref(true);
const loadError = ref(null);

const iconMap = {
    vehicle: 'mdi-car',
    trailer: 'mdi-truck-trailer',
    equipment: 'mdi-tools',
    container: 'mdi-package-variant-closed',
    person: 'mdi-account',
    animal: 'mdi-paw',
    asset: 'mdi-map-marker',
};

const getIcon = (assetType) => {
    const cat = (assetType.category || '').toLowerCase();
    return iconMap[cat] || 'mdi-map-marker';
};

const selectType = (type) => {
    selectedType.value = type;
};

const loadAssetTypes = () => {
    loading.value = true;
    loadError.value = null;
    send('get', 'asset-types', {list: true})
        .then((response) => {
            const data = response.data?.data;
            assetTypes.value = Array.isArray(data) ? data : (data?.data || []);
        })
        .catch(() => {
            loadError.value = 'Unable to load asset types. Please check your connection.';
        })
        .finally(() => {
            loading.value = false;
        });
};

onMounted(loadAssetTypes);
</script>

<style scoped>
.asset-type-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 12px;
}

.asset-type-card {
    transition: all 0.2s ease;
    border: 2px solid transparent;
}

.asset-type-card:hover {
    border-color: rgba(var(--v-theme-primary), 0.25);
    transform: translateY(-2px);
}

.selected-type {
    border-color: rgba(var(--v-theme-primary), 1) !important;
    background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.08), rgba(var(--v-theme-primary), 0.15)) !important;
}

.cursor-pointer {
    cursor: pointer;
}
</style>
